import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCompanyInfoSummary } from '../../api/login';
import { queryKey } from '../../constants/queryKey';
import { useAlert } from '../../hooks/useAlert';
import SysConfigStore from '../../store/common/SysConfigStore';
import { getDomainURL } from '../../util/commonUtils';
import ButtonText from '../ui/buttons/ButtonText';
import PopupSupportEmail from './PopupSupportEmail';

export default function Footer() {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { data: company } = useQuery(queryKey.companyInfoSummary, getCompanyInfoSummary, { staleTime: 60000 });
	const handleToggle = useCallback(() => setOpen(prev => !prev), []);
	const { isComponentEnabled, isModuleEnabled, labelText, sysCompanyConfigs } = SysConfigStore();
	const [snackbar] = useAlert();
	const manualFileName = sysCompanyConfigs.SYSTEM_MANUAL_DOWNLOAD_FILE_NAME;
	const handleClick = useCallback(() => {
		if (isModuleEnabled('DAEKYO_FOOTER'))
			return window.open(
				`${getDomainURL()}/static/tenant/downloadformat/계약관리시스템 신규버전 매뉴얼_최종_21.01.22.pdf`,
			);
		if (isComponentEnabled('CMPNT188_SYSTEM_MANUAL_DOWNLOAD_B') && manualFileName)
			return window.open(`${getDomainURL()}/static/tenant/downloadformat/${manualFileName}`);
		return snackbar(t('cmmn_guide_manual_is_being_prepared'), 'default');
	}, []);

	const handleCopySupportEmail = useCallback(() => {
		const supportMail = labelText('support_mail');
		if (!supportMail) return;

		if (navigator?.clipboard?.writeText) {
			// 표준 clipboard API
			navigator.clipboard
				.writeText(supportMail)
				.then(() => {
					snackbar(t('cmmn_success_copy_email_to_clipboard'), 'success');
				})
				.catch(() => {
					snackbar(t('cmmn_error_failed_to_copy_please_copy_it_yourself'), 'error');
				});
		} else {
			// fallback : execCommand
			const textarea = document.createElement('textarea');
			textarea.value = supportMail;
			textarea.style.position = 'fixed';
			document.body.appendChild(textarea);
			textarea.focus();
			textarea.select();

			try {
				const successful = document.execCommand('copy');
				snackbar(
					successful
						? t('cmmn_success_copy_email_to_clipboard')
						: t('cmmn_error_failed_to_copy_please_copy_it_yourself'),
					successful ? 'success' : 'error',
				);
			} catch (err) {
				snackbar(t('cmmn_error_failed_to_copy_please_copy_it_yourself'), 'error');
			}

			document.body.removeChild(textarea);
		}
	}, []);

	return (
		<footer className='flex flex-col justify-between px-8 py-2 bg-gray-900 lg:flex-row'>
			{!isModuleEnabled('HIDE_MANUAL_DOWNLOAD_BTN') && (
				<div className='flex items-center justify-center'>
					<ButtonText
						className='text-s text-white'
						text={`${labelText('system_manual_name')} ${labelText('system_manual_download')}`}
						onClick={handleClick}
					/>
				</div>
			)}
			<div className='flex items-center justify-center'>
				<PopupSupportEmail
					open={open}
					openTrigger={
						<ButtonText
							className='gap-2 text-s text-white'
							size='small'
							onClick={
								!isModuleEnabled('REMOVE_SUPPORT_EMAIL_POPUP') ? handleToggle : handleCopySupportEmail
							}
						>
							<strong>{t('MSG_ID_404')}</strong>
							<span>{labelText('support_mail')}</span>
						</ButtonText>
					}
					confirm={
						<ButtonText variant='contained' size='small' color='primary'>
							{t('label_confirm')}
						</ButtonText>
					}
					cancel={
						<ButtonText variant='contained' size='small'>
							{t('label_cancellation')}
						</ButtonText>
					}
					onCancel={handleToggle}
					onConfirm={() => handleToggle()}
				/>
				{!isModuleEnabled('REMOVE_ADELE_VERSION_AND_SERVER_ID') && (
					<div className='flex items-center gap-3'>
						<span className='text-s text-white' />
						<span className='text-s text-white'>v2.9</span>
						<span className='text-s text-white'>
							©{company?.name} [{company?.serverId}]
						</span>
					</div>
				)}
			</div>
		</footer>
	);
}
